import { HttpErrorResponse } from '@angular/common/http';

export const AUTH_ERROR_KEYS = {
  generic: 'error.auth.generic',
  network: 'error.auth.network',
  server: 'error.auth.server',
  invalidCredentials: 'error.auth.invalidCredentials',
  accountNotVerified: 'error.auth.accountNotVerified',
  twoFactorUnsupported: 'error.auth.twoFactorUnsupported',
  internalAccount: 'error.auth.internalAccount',
  invalidLink: 'error.auth.invalidLink',
} as const;

export type AuthErrorKey = (typeof AUTH_ERROR_KEYS)[keyof typeof AUTH_ERROR_KEYS];

export interface ParsedHttpError {
  status: number;
  detail: string;
}

/** A dropped connection or a CORS rejection arrives as status 0, not as a real HTTP status. */
const NO_RESPONSE = 0;

export function parseHttpError(error: unknown): ParsedHttpError {
  if (!(error instanceof HttpErrorResponse)) {
    return { status: NO_RESPONSE, detail: '' };
  }
  const body = error.error;
  const detail = typeof body?.detail === 'string' ? body.detail : (body?.message ?? '');
  return { status: error.status, detail: typeof detail === 'string' ? detail : '' };
}

/**
 * Several customer-func routes `return HTTPException(...)` where they meant to `raise` it.
 * FastAPI then encodes the exception object into an ordinary 200 body, so a genuine failure
 * reaches us looking like a success. Detect that shape and treat it as the error it is.
 */
export function decodeEncodedHttpException(body: unknown): ParsedHttpError | null {
  if (!body || typeof body !== 'object') return null;
  const candidate = body as { status_code?: unknown; detail?: unknown };
  if (typeof candidate.status_code !== 'number' || candidate.status_code < 400) return null;
  return {
    status: candidate.status_code,
    detail: typeof candidate.detail === 'string' ? candidate.detail : '',
  };
}

/** Shared fallbacks: anything a flow doesn't map explicitly still gets a usable message. */
export function fallbackErrorKey({ status }: ParsedHttpError): AuthErrorKey {
  if (status === NO_RESPONSE) return AUTH_ERROR_KEYS.network;
  if (status >= 500) return AUTH_ERROR_KEYS.server;
  return AUTH_ERROR_KEYS.generic;
}

export function loginErrorKey(error: unknown): AuthErrorKey {
  const parsed = parseHttpError(error);
  if (parsed.status === 401) {
    // Both cases are 401; only the detail separates "wrong password" from "invite never
    // completed", and the second needs completely different instructions.
    return parsed.detail.includes('not verified')
      ? AUTH_ERROR_KEYS.accountNotVerified
      : AUTH_ERROR_KEYS.invalidCredentials;
  }
  return fallbackErrorKey(parsed);
}

export function resetRequestErrorKey(error: unknown): AuthErrorKey {
  const parsed = parseHttpError(error);
  if (parsed.status === 401) return AUTH_ERROR_KEYS.internalAccount;
  return fallbackErrorKey(parsed);
}

/**
 * The set-password and join-network endpoints validate the emailed token, so every 4xx that
 * isn't the internal-account refusal means the link itself is unusable — expired, already
 * used, or malformed. `isDeadLink` routes those to the link-expired page.
 */
export function tokenFlowError(error: unknown): { key: AuthErrorKey; isDeadLink: boolean } {
  const parsed = parseHttpError(error);
  if (parsed.status === 403) {
    return { key: AUTH_ERROR_KEYS.internalAccount, isDeadLink: false };
  }
  if (parsed.status === 400 || parsed.status === 404) {
    return { key: AUTH_ERROR_KEYS.invalidLink, isDeadLink: true };
  }
  return { key: fallbackErrorKey(parsed), isDeadLink: false };
}
